// OrderDetailsPage.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import { connect, useDispatch } from 'react-redux';
import {
  getOrderDetails,
  getOrderProposals,
  updateOrderProposal,
  sendOrderProposal,
  markOrderAsDone,
  completeOrder,
  updateHandymanProposal,
  getHandymanOrderProposals,
  sendHandymanProposal,
} from './actions';
import {
  OrderViewModel,
  OrderAttributeAnswerViewModel,
  OrderProposalViewModel,
  CustomerOrderStatus,
  HandymanOrderStatus,
  OrderHandymanProposalsViewModel,
  OrderProposalStatus,
  ReviewStatus,
} from '../../configuration/api/apiSchema';
import ChatIcon from '@mui/icons-material/Chat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import HandymanIcon from '@mui/icons-material/Handyman';
import { HandymanAppState } from '../../configuration/redux/reducers';
import PreviewImageModal from '../../components/PreviewImage/PreviewImageModal';
import RateReviewIcon from '@mui/icons-material/RateReview';
import StarIcon from '@mui/icons-material/Star';
import CreateReviewForm from '../../components/Reviews/CreateReviewForm';
import ReviewsList from '../../components/Reviews/ReviewsList';
import ClickableAvatar from '../../components/ClickableAvatar/ClickableAvatar';
import CloseIcon from '@mui/icons-material/Close';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';

// NEW: BrowseHandyman (similar to CreateOrderPage approach)
import { createOrGetChat } from './actions';
import { createCheckoutSession } from './actions';
import { getOrderReviews, createReview } from '../Reviews/actions';
import {
  CreateReviewCommand,
  ReviewType,
} from '../../configuration/api/apiSchema';
import { loadStripe } from '@stripe/stripe-js';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../configuration/api/apiHelper';
import {
  GlassProposalCard,
  GlassStatusChip,
  GlassButton,
} from '../../components/glassmorphism/GlassmorphismStyles';
import ProposalPriceModal from '../../components/ProposalPriceModal';
import SelectHandymanTrigger from './SelectHandymanTrigger';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);
type AppOrderStatus = CustomerOrderStatus | HandymanOrderStatus;
export const mapOrderStatusToString = (
  orderStatus?: AppOrderStatus,
  role?: string,
): string => {
  if (role === 'handyman' && orderStatus === 'Applied') {
    return 'Proposal Sent';
  }
  switch (orderStatus) {
    case CustomerOrderStatus.AwaitingProposals:
      return 'Awaiting Proposals';
    case CustomerOrderStatus.InProgress:
      return 'In Progress';
    case CustomerOrderStatus.ActionRequired:
      return 'Action Required';
    case CustomerOrderStatus.Completed:
      return 'Completed';
    case CustomerOrderStatus.Canceled:
      return 'Canceled';
    default:
      return 'Unknown Status';
  }
};
// Helper to get order status color (same as OrdersPage)
export const getOrderStatusColor = (
  customerStatus: AppOrderStatus | undefined,
) => {
  switch (customerStatus) {
    case CustomerOrderStatus.AwaitingProposals:
      return '#005CAD';
    case CustomerOrderStatus.InProgress:
      return '#005CAD';
    case CustomerOrderStatus.ActionRequired:
      return '#5C00AD';
    case CustomerOrderStatus.Canceled:
      return '#C40003';
    case CustomerOrderStatus.Completed:
      return '#29A322';
    default:
      return '#005CAD';
  }
};
const getDaysAgoLabel = (createdAtUtc: string | undefined): string => {
  if (!createdAtUtc) {
    return '';
  }

  const createdDate = new Date(createdAtUtc);
  const now = new Date();
  const diffMs = now.getTime() - createdDate.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Sent today';
  if (days === 1) return 'Sent 1 day ago';
  return `Sent ${days} days ago`;
};
interface StateFromProps {
  orderDetails: OrderViewModel | null;
  orderDetailsLoading: boolean;
  orderDetailsError: string | null;
  orderProposals: OrderProposalViewModel[];
  orderProposalsLoading: boolean;
  handymanProposals: OrderProposalViewModel[];
  handymanProposalsLoading: boolean;
  handymanOrderProposals: OrderProposalViewModel[];
  handymanOrderProposalsLoading: boolean;
  role: string; // 'customer' or 'handyman'
  currentUserId?: string;
  orderReviews: any[];
  orderReviewsLoading: boolean;
  orderReviewsTotalCount: number;
  orderReviewsError: string | null;
  currentPage: number;
}

interface DispatchFromProps {
  getOrderDetails: (orderId: string) => void;
  getOrderProposals: (orderId: string) => void;
  updateOrderProposal: (
    orderProposalId: string,
    status: 'Confirmed' | 'Rejected',
  ) => void;
  sendOrderProposal: (orderId: string, handymanId: string) => void;
  getOrderReviews: (orderId: string) => void;
}

type Props = StateFromProps & DispatchFromProps;

const OrderDetailsPage: React.FC<Props> = (props: Props) => {
  const { orderId } = useParams<{ orderId: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [searchParams, setSearchParams] = useSearchParams();

  const sessionId = searchParams.get('session_id');
  const canceled = searchParams.get('canceled');

  const {
    orderDetails,
    orderDetailsLoading,
    orderDetailsError,
    orderProposals: customerProposals,
    orderProposalsLoading: customerProposalsLoading,
    handymanOrderProposals,
    handymanOrderProposalsLoading,
    role,
    currentUserId,
    orderReviews,
    orderReviewsLoading,
    orderReviewsError,
    currentPage,
  } = props;

  const isNotHandyman = role !== 'handyman';

  const orderProposals =
    role === 'handyman' ? handymanOrderProposals : customerProposals;
  const orderProposalsLoading =
    role === 'handyman'
      ? handymanOrderProposalsLoading
      : customerProposalsLoading;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const uniqProposals = Array.from(
    new Map(
      [...orderProposals].reverse().map((p) => [p.handymanId, p]),
    ).values(),
  ).filter((p) => p.status === 'Requested');

  const handymenInvited = uniqProposals
    .filter((p) => p.isInitiatedByCustomer)
    .map((p) => ({
      handymanId: p.handymanId,
      fullName:
        `${p.handyman?.firstName ?? ''} ${p.handyman?.lastName ?? ''}`.trim(),
    }));

  const handymenApplied = uniqProposals
    .filter((p) => !p.isInitiatedByCustomer)
    .map((p) => ({
      handymanId: p.handymanId,
      fullName:
        `${p.handyman?.firstName ?? ''} ${p.handyman?.lastName ?? ''}`.trim(),
    }));

  const [invitedHandymen, setInvitedHandymen] = useState<
    OrderHandymanProposalsViewModel[]
  >([]);

  // Modal state for price proposals
  const [showProposalModal, setShowProposalModal] = useState<boolean>(false);
  const [applyingToOrder, setApplyingToOrder] = useState<boolean>(false);

  const fetchInvitedHandymen = async () => {
    if (!orderId) return;

    try {
      const response = await api.orders.handymanProposalsDetail(orderId);
      setInvitedHandymen(response.data || []);
    } catch (error) {
      console.error('Failed to fetch invited handymen:', error);
    }
  };

  const handlePayNow = async () => {
    if (!orderId) return;

    try {
      const response = await dispatch(createCheckoutSession(orderId) as any);
      if (!response.sessionId) {
        throw new Error('Session ID not returned');
      }

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe.js failed to load');
      }

      await stripe.redirectToCheckout({ sessionId: response.sessionId });
    } catch (error) {
      console.error('Failed to create checkout session', error);
      openSnackbar('Failed to start checkout', 'error');
    }
  };

  const handleChatWithHandyman = async (
    e: React.MouseEvent,
    handymanId: string,
  ) => {
    e.stopPropagation();
    if (!currentUserId) return;

    try {
      const chatId: any = await dispatch(
        createOrGetChat([currentUserId, handymanId]) as any,
      );
      if (chatId) {
        navigate(`/chats/${chatId}`);
      }
    } catch (error) {
      console.error('Failed to create or get chat:', error);
    }
  };

  useEffect(() => {
    if (sessionId) {
      openSnackbar('✅ Payment completed successfully!', 'success');
      if (orderId) {
        (dispatch as any)(getOrderDetails(orderId));
      }
      setTimeout(() => {
        searchParams.delete('session_id');
        navigate(`/orders/${orderId}`, { replace: true });
      }, 1000);
    }
    if (canceled) {
      openSnackbar('❌ Payment was canceled.', 'error');
      setTimeout(() => {
        searchParams.delete('canceled');
        navigate(`/orders/${orderId}`, { replace: true });
      }, 1000);
    }
  }, [sessionId, canceled, orderId]);

  useEffect(() => {
    if (orderId) {
      props.getOrderDetails(orderId);
      fetchInvitedHandymen();
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId && role === 'customer') {
      props.getOrderProposals(orderId);
    } else if (orderId && role === 'handyman') {
      dispatch(getHandymanOrderProposals(orderId) as any);
    }
  }, [orderId, role]);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (base64Image: string) => {
    setSelectedImage(base64Image);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  const handleApproveProposal = async (proposalId: string) => {
    try {
      await dispatch(updateOrderProposal(proposalId, 'Confirmed') as any);
      openSnackbar(
        'Proposal approved! Other proposals have been automatically cancelled.',
        'success',
      );
      if (orderId) {
        await dispatch(getOrderDetails(orderId) as any);
        if (role === 'customer') {
          await dispatch(getOrderProposals(orderId) as any);
        } else if (role === 'handyman') {
          await dispatch(getHandymanOrderProposals(orderId) as any);
        }
        await fetchInvitedHandymen();
      }
    } catch (err) {
      openSnackbar('Failed to approve proposal', 'error');
    }
  };

  const handleRejectProposal = async (proposalId: string) => {
    try {
      await dispatch(updateOrderProposal(proposalId, 'Rejected') as any);
      openSnackbar('Proposal rejected', 'success');
      if (orderId) {
        if (role === 'customer') {
          await dispatch(getOrderProposals(orderId) as any);
        } else if (role === 'handyman') {
          await dispatch(getHandymanOrderProposals(orderId) as any);
        }
      }
    } catch (err) {
      openSnackbar('Failed to reject proposal', 'error');
    }
  };

  const handleAcceptProposal = async (proposalId: string) => {
    try {
      await dispatch(updateHandymanProposal(proposalId, 'Confirmed') as any);
      trackEvent('customer_proposal_accepted', {
        order_id: orderId,
        proposal_id: proposalId,
      });
      openSnackbar(
        'Proposal accepted! Other proposals have been automatically cancelled.',
        'success',
      );
      if (orderId) {
        await dispatch(getOrderDetails(orderId) as any);
        if (role === 'customer') {
          await dispatch(getOrderProposals(orderId) as any);
        } else if (role === 'handyman') {
          await dispatch(getHandymanOrderProposals(orderId) as any);
        }
        await fetchInvitedHandymen();
      }
    } catch (err) {
      openSnackbar('Failed to accept proposal', 'error');
    }
  };

  const handleDeclineProposal = async (proposalId: string) => {
    try {
      await dispatch(updateHandymanProposal(proposalId, 'Rejected') as any);
      openSnackbar('Proposal rejected!', 'success');
      if (orderId) {
        dispatch(getOrderDetails(orderId) as any);
        dispatch(getOrderProposals(orderId) as any);
      }
    } catch (err) {
      openSnackbar('Failed to reject proposal', 'error');
    }
  };

  const handleSelectHandyman = async (handymanId: string) => {
    if (!orderId) return;

    try {
      trackEvent('customer_invitation_sent', {
        order_id: orderId,
        handyman_id: handymanId,
      });
      await dispatch(sendOrderProposal(orderId, handymanId) as any);
      openSnackbar('Proposal sent!', 'success');

      await dispatch(getOrderDetails(orderId) as any);
      await dispatch(getOrderProposals(orderId) as any);
      await fetchInvitedHandymen();
    } catch {
      openSnackbar('Failed to send proposal', 'error');
    }
  };

  const handleApplyToOrder = () => {
    setShowProposalModal(true);
  };

  const handleProposalSubmit = async (
    orderId: string,
    proposedPrice?: number,
    coverLetter?: string,
  ) => {
    setApplyingToOrder(true);
    try {
      await dispatch(
        sendHandymanProposal(orderId, proposedPrice, coverLetter) as any,
      );
      trackEvent('handyman_proposal_sent', {
        order_id: orderId,
        has_custom_price: String(!!proposedPrice),
      });
      setShowProposalModal(false);
      openSnackbar('Proposal sent successfully!', 'success');
      await dispatch(getOrderDetails(orderId) as any);
      await dispatch(getHandymanOrderProposals(orderId) as any);
    } catch (error) {
      openSnackbar('Failed to send proposal', 'error');
    } finally {
      setApplyingToOrder(false);
    }
  };

  const handleProposalModalClose = () => {
    setShowProposalModal(false);
  };

  const hasAlreadyApplied = () => {
    if (!currentUserId || !orderProposals) return false;
    return orderProposals.some(
      (proposal) =>
        proposal.handymanId === currentUserId &&
        !proposal.isInitiatedByCustomer,
    );
  };

  // Check if there's a proposal invitation from customer for current handyman
  const hasInvitation = () => {
    if (!currentUserId || !orderProposals) return false;
    return orderProposals.some(
      (proposal) =>
        proposal.handymanId === currentUserId &&
        proposal.isInitiatedByCustomer === true &&
        proposal.status === OrderProposalStatus.Requested,
    );
  };

  // Get the invitation proposal
  const getInvitationProposal = () => {
    if (!currentUserId || !orderProposals) return null;
    return orderProposals.find(
      (proposal) =>
        proposal.handymanId === currentUserId &&
        proposal.isInitiatedByCustomer === true &&
        proposal.status === OrderProposalStatus.Requested,
    );
  };

  const renderOrderAttributes = (answers: OrderAttributeAnswerViewModel[]) => {
    return answers.map((ans, idx) => {
      const propertyName =
        ans.categoryAttribute?.translatedAtributeContent?.label || 'Property';
      const value = ans.value || 'N/A';
      return (
        <Typography key={idx} variant='body1' sx={{ mt: 1 }}>
          <strong>{propertyName}:</strong> {value}
        </Typography>
      );
    });
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success',
  );
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const openSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleMarkAsDone = async () => {
    if (!orderId) return;

    try {
      await dispatch(markOrderAsDone(orderId) as any);
      openSnackbar('Order has been marked as done!', 'success');
      await dispatch(getOrderDetails(orderId) as any);
      if (role === 'handyman') {
        await dispatch(getHandymanOrderProposals(orderId) as any);
      }
    } catch (err) {
      openSnackbar('Failed to mark order as done', 'error');
    }
  };

  const handleCompleteOrder = async () => {
    if (!orderId) return;

    try {
      await dispatch(completeOrder(orderId) as any);
      openSnackbar('Order is now completed!', 'success');
      await dispatch(getOrderDetails(orderId) as any);
      if (role === 'customer') {
        await dispatch(getOrderProposals(orderId) as any);
      }
    } catch (err) {
      openSnackbar('Failed to complete order', 'error');
    }
  };

  function getHandymanSection(): React.ReactNode {
    if (!orderDetails) {
      return;
    }

    return (
      <Box mt={4}>
        <Typography variant='h6' gutterBottom>
          Handyman
        </Typography>
        {orderDetails.handyman ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ClickableAvatar
              userId={orderDetails.handyman.id}
              src={
                orderDetails.handyman.avatarUrl ||
                'https://via.placeholder.com/40'
              }
              alt={`${orderDetails.handyman.firstName} ${orderDetails.handyman.lastName}`}
              sx={{ width: 30, height: 30 }}
            />
            <Typography variant='body1'>
              {orderDetails.handyman.firstName +
                ' ' +
                orderDetails.handyman.lastName}
            </Typography>
            <IconButton
              color='primary'
              onClick={(e) =>
                handleChatWithHandyman(e, orderDetails.handyman?.id ?? '')
              }
              title='Chat with Handyman'
            >
              <ChatIcon />
            </IconButton>
          </Box>
        ) : (
          <Box>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 1 }}>
              No handyman selected yet
            </Typography>
            {handymenInvited.length > 0 ? (
              <Typography variant='body2' color='text.secondary'>
                Invited handymen:{' '}
                {handymenInvited
                  .map((h) => h.fullName)
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No handymen have been invited yet
              </Typography>
            )}

            {handymenApplied.length > 0 ? (
              <Typography variant='body2' color='text.secondary'>
                Applied handymen:{' '}
                {handymenApplied
                  .map((h) => h.fullName)
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No handymen have been applied yet
              </Typography>
            )}
          </Box>
        )}

        {!orderDetails.handyman && (
          <Box mt={2}>
            <SelectHandymanTrigger
              variant='button'
              preselectedCategoryKey={orderDetails?.category?.key ?? undefined}
              isNotHandyman={isNotHandyman}
              excludeFilters={['price-range', 'location', 'name', 'categories']}
              address={orderDetails?.address ?? null}
              actionButtonTitle='Select'
              onSelect={handleSelectHandyman}
            >
              <Button variant='outlined'>Select Handyman</Button>
            </SelectHandymanTrigger>
          </Box>
        )}
      </Box>
    );
  }
  function getCustomerSection(): React.ReactNode {
    if (!orderDetails?.customer) {
      return;
    }

    return (
      <Box mt={4}>
        <Typography variant='h6' gutterBottom>
          Customer
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ClickableAvatar
            userId={orderDetails.customer.id}
            src={
              orderDetails.customer.avatarUrl ||
              'https://via.placeholder.com/40'
            }
            alt={`${orderDetails.customer.firstName} ${orderDetails.customer.lastName}`}
            sx={{ width: 30, height: 30 }}
          />
          <Typography variant='body1'>
            {orderDetails.customer.firstName +
              ' ' +
              orderDetails.customer.lastName}
          </Typography>
          <IconButton
            color='primary'
            onClick={(e) =>
              handleChatWithHandyman(e, orderDetails.customer?.id ?? '')
            }
            title='Chat with customer'
          >
            <ChatIcon />
          </IconButton>
        </Box>
      </Box>
    );
  }

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderReviews(orderId) as any);
    }
  }, [dispatch, orderId]);

  const getAverageRating = () => {
    if (!orderReviews || orderReviews.length === 0) return 0;
    const sum = orderReviews.reduce(
      (acc, review) => acc + (review.rating || 0),
      0,
    );
    return sum / orderReviews.length;
  };

  const [showCreateReview, setShowCreateReview] = useState(false);

  const getReviewsSection = () => {
    if (
      !orderDetails ||
      orderDetails.customerStatus !== CustomerOrderStatus.Completed
    )
      return null;

    const submittedReviews = orderReviews.filter(
      (review) => review.status === ReviewStatus.Submitted,
    );

    if (submittedReviews.length === 0 && !canLeaveReview()) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: { xs: '16px', sm: '20px', md: '24px 24px 32px' },
          gap: { xs: '16px', md: '24px' },
          width: '100%',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
          borderRadius: { xs: '24px', md: '32px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            width: '100%',
            gap: { xs: '12px', md: '16px' },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: { xs: '20px', md: '24px' },
              lineHeight: '133.4%',
              color: isDark ? '#00FFFF' : '#005CAD',
            }}
          >
            Reviews
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canLeaveReview() && (
              <GlassButton
                variant='outlined'
                startIcon={<RateReviewIcon />}
                onClick={() => setShowCreateReview(true)}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontWeight: 600,
                  fontFamily: '"Montserrat", sans-serif',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {getReviewButtonLabel()}
              </GlassButton>
            )}
            {submittedReviews.length > 0 && (
              <Button
                component={Link}
                to={`/reviews/order/${orderId}`}
                variant='outlined'
                sx={buttonStyles.outlined}
              >
                View All Reviews ({submittedReviews.length})
              </Button>
            )}
          </Box>
        </Box>
        {submittedReviews.length > 0 ? (
          <ReviewsList
            reviews={submittedReviews.slice(0, 3)}
            loading={orderReviewsLoading}
            error={orderReviewsError}
            totalCount={submittedReviews.length}
            showActions={false}
            emptyMessage='No reviews yet'
            title=''
            currentPage={1}
            onPageChange={() => {}}
          />
        ) : (
          <Typography
            sx={{
              color: isDark ? '#B6B6B6' : '#47536B',
              fontFamily: "'Roboto', sans-serif",
            }}
          >
            No reviews yet. Be the first to leave a review!
          </Typography>
        )}
      </Box>
    );
  };

  const canLeaveReview = () => {
    if (!currentUserId || !orderDetails) return false;
    if (orderDetails.customerStatus !== CustomerOrderStatus.Completed)
      return false;

    const isCustomer = currentUserId === orderDetails.customer?.id;
    const isHandyman = currentUserId === orderDetails.handyman?.id;
    if (!isCustomer && !isHandyman) return false;

    const userReview = orderReviews.find(
      (review) => review.reviewer?.id === currentUserId,
    );
    return !userReview || userReview.status === ReviewStatus.Pending;
  };

  const getRevieweeId = () => {
    if (!currentUserId || !orderDetails) return '';

    const isCustomer = currentUserId === orderDetails.customer?.id;
    return isCustomer
      ? orderDetails.handyman?.id || ''
      : orderDetails.customer?.id || '';
  };

  const getReviewType = () => {
    if (!currentUserId || !orderDetails) return ReviewType.CustomerToHandyman;

    const isCustomer = currentUserId === orderDetails.customer?.id;
    return isCustomer
      ? ReviewType.CustomerToHandyman
      : ReviewType.HandymanToCustomer;
  };

  const handleSubmitReview = async (data: CreateReviewCommand) => {
    try {
      await dispatch(createReview(data) as any);
      setShowCreateReview(false);
      openSnackbar('Review submitted successfully!', 'success');
      if (orderId) {
        await dispatch(getOrderDetails(orderId) as any);
        await dispatch(getOrderReviews(orderId, currentPage) as any);
      }
    } catch (error) {
      openSnackbar('Failed to submit review', 'error');
    }
  };

  const handlePageChange = (page: number) => {
    if (orderId) {
      dispatch(getOrderReviews(orderId, page) as any);
    }
  };

  const shouldShowRightColumn = () => {
    if (role === 'customer') return true;
    if (role === 'handyman' && orderDetails) {
      return orderDetails.handyman?.id === currentUserId;
    }
    return false;
  };

  const rightColumnVisible = shouldShowRightColumn();

  const isCurrentUserAssignedHandyman = () => {
    if (!orderDetails || !currentUserId) return false;
    return orderDetails.handyman?.id === currentUserId;
  };

  const needsPayment = () => {
    const needToPay =
      orderDetails?.customerStatus === CustomerOrderStatus.Completed &&
      orderDetails?.paymentStatus !== 'Paid';
    return needToPay;
  };

  const buttonStyles = {
    primary: {
      background: 'linear-gradient(135deg, #005CAD 0%, #00E8E7 100%)',
      color: 'white',
      fontWeight: 600,
      fontFamily: '"Montserrat", sans-serif',
      '&:hover': {
        background: 'linear-gradient(135deg, #004A91 0%, #00C5C4 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 8px 16px rgba(0, 232, 231, 0.3)',
      },
    },
    success: {
      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
      color: 'white',
      fontWeight: 600,
      fontFamily: '"Montserrat", sans-serif',
      '&:hover': {
        background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 8px 16px rgba(76, 175, 80, 0.3)',
      },
    },
    outlined: {
      borderColor: 'rgba(0, 92, 173, 0.3)',
      color: 'rgba(0, 92, 173, 0.9)',
      fontWeight: 600,
      fontFamily: '"Montserrat", sans-serif',
      '&:hover': {
        borderColor: 'rgba(0, 92, 173, 0.5)',
        backgroundColor: 'rgba(0, 92, 173, 0.1)',
        transform: 'translateY(-1px)',
      },
    },
    disabled: {
      background: 'rgba(0, 92, 173, 0.5)',
      color: 'rgba(255, 255, 255, 0.7)',
    },
  };

  const createChatButton = (userId: string, label: string, key: string) => (
    <GlassButton
      key={key}
      variant='outlined'
      startIcon={<ChatIcon />}
      onClick={(e: React.MouseEvent) => handleChatWithHandyman(e, userId)}
      sx={{
        borderColor: 'rgba(255, 255, 255, 0.3)',
        color: 'white',
        fontWeight: 600,
        fontFamily: '"Montserrat", sans-serif',
        '&:hover': {
          borderColor: 'rgba(255, 255, 255, 0.5)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {label}
    </GlassButton>
  );

  const getReviewButtonLabel = () => {
    if (!currentUserId || !orderDetails) return 'Leave Review';
    const isCustomer = currentUserId === orderDetails.customer?.id;
    return isCustomer ? 'Review Handyman' : 'Review Customer';
  };

  const createReviewButton = (key: string) => (
    <GlassButton
      key={key}
      variant='outlined'
      startIcon={<RateReviewIcon />}
      onClick={() => setShowCreateReview(true)}
      sx={{
        borderColor: 'rgba(255, 255, 255, 0.3)',
        color: 'white',
        fontWeight: 600,
        fontFamily: '"Montserrat", sans-serif',
        '&:hover': {
          borderColor: 'rgba(255, 255, 255, 0.5)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {getReviewButtonLabel()}
    </GlassButton>
  );

  const renderOrderActions = () => {
    if (!orderDetails) return null;

    const actions: React.ReactNode[] = [];
    const isCustomer = role === 'customer';
    const isHandyman = role === 'handyman';
    const { customerStatus, handyman, customer } = orderDetails;

    if (isCustomer) {
      if (customerStatus === CustomerOrderStatus.AwaitingProposals) {
        if (!handyman) {
          actions.push(
            <SelectHandymanTrigger
              variant='button'
              preselectedCategoryKey={orderDetails?.category?.key ?? undefined}
              isNotHandyman={isNotHandyman}
              excludeFilters={['price-range', 'location', 'name', 'categories']}
              address={orderDetails?.address ?? null}
              actionButtonTitle='Select'
              onSelect={handleSelectHandyman}
            >
              <GlassButton
                key='select-handyman'
                variant='contained'
                startIcon={<HandymanIcon />}
                sx={buttonStyles.primary}
              >
                Select Handyman
              </GlassButton>
            </SelectHandymanTrigger>,
          );
        } else {
          actions.push(
            createChatButton(
              handyman.id ?? '',
              'Chat with Handyman',
              'chat-handyman',
            ),
          );
        }
      }

      if (customerStatus === CustomerOrderStatus.InProgress && handyman) {
        actions.push(
          createChatButton(
            handyman.id ?? '',
            'Chat with Handyman',
            'chat-handyman',
          ),
        );
      }

      if (customerStatus === CustomerOrderStatus.ActionRequired) {
        actions.push(
          <GlassButton
            key='complete-order'
            variant='contained'
            startIcon={<CheckCircleIcon />}
            onClick={handleCompleteOrder}
            sx={buttonStyles.success}
          >
            Complete Order
          </GlassButton>,
        );
      }

      // if (status === OrderStatus.Completed) {
      if (needsPayment()) {
        actions.push(
          <GlassButton
            key='pay-now'
            variant='contained'
            startIcon={<PaymentIcon />}
            onClick={handlePayNow}
            sx={buttonStyles.primary}
          >
            Pay Now
          </GlassButton>,
        );
      }
      if (canLeaveReview()) {
        actions.push(createReviewButton('leave-review'));
      }
      // }
    }

    if (isHandyman) {
      // Check if there's an invitation from customer
      const invitationProposal = getInvitationProposal();
      if (
        invitationProposal &&
        customerStatus === CustomerOrderStatus.AwaitingProposals &&
        !handyman
      ) {
        actions.push(
          <GlassButton
            key='accept-invitation'
            variant='contained'
            startIcon={<CheckCircleIcon />}
            onClick={() => handleAcceptProposal(invitationProposal.id!)}
            sx={buttonStyles.success}
          >
            Accept Invitation
          </GlassButton>,
        );
        actions.push(
          <GlassButton
            key='decline-invitation'
            variant='outlined'
            startIcon={<ThumbDownIcon />}
            onClick={() => handleDeclineProposal(invitationProposal.id!)}
            sx={{
              borderColor: 'rgba(244, 67, 54, 0.3)',
              color: 'rgba(244, 67, 54, 0.9)',
              '&:hover': {
                borderColor: 'rgba(244, 67, 54, 0.5)',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
              },
            }}
          >
            Decline
          </GlassButton>,
        );
        // Add chat button when there's an invitation
        if (customer) {
          actions.push(
            createChatButton(
              customer.id ?? '',
              'Chat with Customer',
              'chat-customer-invitation',
            ),
          );
        }
      } else if (
        customerStatus === CustomerOrderStatus.AwaitingProposals &&
        !hasAlreadyApplied() &&
        !handyman
      ) {
        actions.push(
          <GlassButton
            key='apply-order'
            variant='contained'
            startIcon={<SendIcon />}
            onClick={handleApplyToOrder}
            disabled={applyingToOrder}
            sx={{
              ...buttonStyles.primary,
              ...(applyingToOrder && buttonStyles.disabled),
            }}
          >
            {applyingToOrder ? 'Applying...' : 'Apply to Order'}
          </GlassButton>,
        );
        // Add chat button for New orders when handyman is viewing to apply
        if (customer) {
          actions.push(
            createChatButton(
              customer.id ?? '',
              'Chat with Customer',
              'chat-customer-new',
            ),
          );
        }
      }

      if (customerStatus === CustomerOrderStatus.InProgress) {
        if (isCurrentUserAssignedHandyman()) {
          actions.push(
            <GlassButton
              key='mark-done'
              variant='contained'
              startIcon={<DoneIcon />}
              onClick={handleMarkAsDone}
              sx={buttonStyles.success}
            >
              Mark as Done
            </GlassButton>,
          );
        }
        if (customer) {
          actions.push(
            createChatButton(
              customer.id ?? '',
              'Chat with Customer',
              'chat-customer',
            ),
          );
        }
      }

      if (
        (customerStatus === CustomerOrderStatus.ActionRequired ||
          customerStatus === CustomerOrderStatus.Completed) &&
        customer
      ) {
        actions.push(
          createChatButton(
            customer.id ?? '',
            'Chat with Customer',
            'chat-customer',
          ),
        );
      }

      if (
        customerStatus === CustomerOrderStatus.Completed &&
        canLeaveReview()
      ) {
        actions.push(createReviewButton('leave-review'));
      }
    }

    if (actions.length === 0) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: { xs: '16px', sm: '20px', md: '24px 24px 32px' },
          gap: { xs: '16px', md: '24px' },
          width: '100%',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
          borderRadius: { xs: '24px', md: '32px' },
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontStyle: 'normal',
            fontWeight: 700,
            fontSize: { xs: '20px', md: '24px' },
            lineHeight: '133.4%',
            color: isDark ? '#00FFFF' : '#005CAD',
          }}
        >
          Order Actions
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '12px', sm: '16px' },
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {actions}
        </Box>
      </Box>
    );
  };

  const getProposalsSection = () => {
    // Only customers should see the full proposals section
    if (!orderDetails || (role as string) !== 'customer') return null;

    return (
      <Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Typography
            variant='h6'
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 700,
              color: 'rgba(26, 26, 26, 0.9)',
            }}
          >
            Proposals
          </Typography>
          <Link
            to={`/proposals/order/${orderId}`}
            style={{ textDecoration: 'none' }}
          >
            <GlassButton
              variant='contained'
              startIcon={<HandshakeIcon />}
              sx={{
                background: 'linear-gradient(135deg, #005CAD 0%, #00E8E7 100%)',
                color: 'white',
                fontWeight: 600,
                fontFamily: '"Montserrat", sans-serif',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #004A91 0%, #00C5C4 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 16px rgba(0, 232, 231, 0.3)',
                },
              }}
            >
              View All Proposals ({orderProposals.length})
            </GlassButton>
          </Link>
        </Box>

        {orderProposalsLoading ? (
          <Box display='flex' justifyContent='center' p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : orderProposals.length === 0 ? (
          <Typography
            sx={{
              color: 'rgba(26, 26, 26, 0.7)',
              fontFamily: '"Roboto", sans-serif',
              p: 2,
            }}
          >
            No proposals yet
          </Typography>
        ) : (
          <Box>
            {orderProposals.slice(0, 2).map((proposal) => {
              const isSentByCurrentUser =
                (proposal.isInitiatedByCustomer &&
                  currentUserId === proposal.customerId) ||
                (!proposal.isInitiatedByCustomer &&
                  currentUserId === proposal.handymanId);

              return (
                <GlassProposalCard
                  key={proposal.id}
                  className={proposal.status?.toLowerCase()}
                  sx={{ p: 2.5, mb: 2 }}
                >
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ position: 'relative', zIndex: 1 }}
                  >
                    <Box display='flex' alignItems='center' gap={1.5}>
                      <HandymanIcon
                        sx={{
                          color: '#005CAD',
                          fontSize: '20px',
                        }}
                      />
                      <Typography
                        variant='subtitle1'
                        sx={{
                          fontFamily: '"Montserrat", sans-serif',
                          fontWeight: 600,
                          color: 'rgba(26, 26, 26, 0.9)',
                        }}
                      >
                        {proposal?.handyman
                          ? `${proposal.handyman.firstName} ${proposal.handyman.lastName}`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <GlassStatusChip
                      label={isSentByCurrentUser ? 'Sent' : 'Received'}
                      size='small'
                      status={isSentByCurrentUser ? 'sent' : 'received'}
                      sx={{
                        backgroundColor: isSentByCurrentUser
                          ? 'rgba(0, 92, 173, 0.2)'
                          : 'rgba(0, 232, 231, 0.2)',
                        borderColor: isSentByCurrentUser
                          ? 'rgba(0, 92, 173, 0.3)'
                          : 'rgba(0, 232, 231, 0.3)',
                      }}
                    />
                  </Box>
                  <Box mt={1.5} sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'rgba(26, 26, 26, 0.7)',
                        fontFamily: '"Roboto", sans-serif',
                      }}
                    >
                      Status:{' '}
                      <Box
                        component='span'
                        sx={{
                          fontWeight: 600,
                          color:
                            proposal.status === OrderProposalStatus.Confirmed
                              ? '#4CAF50'
                              : proposal.status === OrderProposalStatus.Rejected
                                ? '#F44336'
                                : proposal.status ===
                                    OrderProposalStatus.Cancelled
                                  ? '#9E9E9E'
                                  : '#FF9800',
                        }}
                      >
                        {proposal.status}
                      </Box>
                    </Typography>
                  </Box>
                </GlassProposalCard>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // Add this component for the skeleton loader
  const OrderDetailsSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Order Details Section Skeleton */}
      <Box
        sx={{
          padding: '24px 24px 32px',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          borderRadius: '32px',
          boxShadow:
            '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05)',
        }}
      >
        {/* Title */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Skeleton variant='text' width={167} height={32} />
        </Box>

        {/* Two Cards Container */}
        <Box
          sx={{
            display: 'flex',
            gap: '30px',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Left Card */}
          <Box
            sx={{
              flex: 1,
              padding: '24px',
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              borderRadius: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: '16px', mb: i < 3 ? 2 : 0 }}
              >
                <Skeleton variant='circular' width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant='text' width={60} height={22} />
                  <Skeleton variant='text' width='80%' height={24} />
                </Box>
              </Box>
            ))}
          </Box>

          {/* Right Card */}
          <Box
            sx={{
              flex: 1,
              padding: '24px',
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              borderRadius: '32px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: '16px', mb: i < 3 ? 2 : 0 }}
              >
                <Skeleton variant='circular' width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant='text' width={80} height={22} />
                  <Skeleton variant='text' width='70%' height={24} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Handyman Section */}
        <Box sx={{ mb: 2 }}>
          <Skeleton variant='text' width={100} height={28} sx={{ mb: 1 }} />
          <Skeleton variant='text' width={200} height={24} />
        </Box>

        {/* Order Photos Section */}
        <Box>
          <Skeleton variant='text' width={120} height={29} sx={{ mb: 1 }} />
          <Skeleton variant='text' width={156} height={21} />
        </Box>
      </Box>

      {/* Proposals Section Skeleton */}
      <Box
        sx={{
          padding: '24px 24px 32px',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          borderRadius: '64px',
          boxShadow:
            '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Skeleton
            variant='text'
            width={124}
            height={32}
            sx={{ margin: '0 auto' }}
          />
          <Skeleton
            variant='rectangular'
            width={226}
            height={48}
            sx={{ borderRadius: '64px' }}
          />
        </Box>

        {/* Proposal Cards */}
        {[1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              padding: '24px',
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              borderRadius: '32px',
              mb: i < 2 ? 2 : 0,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <Skeleton variant='circular' width={40} height={40} />
                <Box>
                  <Skeleton variant='text' width={120} height={32} />
                  <Skeleton variant='text' width={90} height={18} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <Skeleton
                  variant='rectangular'
                  width={59}
                  height={22}
                  sx={{ borderRadius: '999px' }}
                />
                <Skeleton variant='text' width={68} height={20} />
              </Box>
            </Box>
            <Skeleton variant='text' width='100%' height={24} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 8, md: 10 },
        px: { xs: 2, sm: 3, md: 4 },
        // background: isDark
        //   ? '#1A1A1A'
        //   : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <Box
        sx={{
          maxWidth: '1152px',
          width: '100%',
          mx: 'auto',
          mt: { xs: 2, md: 4 },
        }}
      >
        {/* Image Preview Modal */}
        <PreviewImageModal
          base64SelectedImage={selectedImage}
          isImageModalOpen={isImageModalOpen}
          handleCloseImageModal={handleCloseImageModal}
        />

        {/* Browse Handyman Modal */}
        {/* <Dialog
          open={browseHandymanOpen}
          onClose={handleCloseBrowseHandyman}
          fullWidth
          maxWidth='md'
        >
          <DialogTitle>Select a Handyman</DialogTitle>
          <DialogContent>
            <GoogleMapsWrapper>
              <BrowseHandymanList
                preselectedCategoryKey={
                  orderDetails?.category?.key ?? undefined
                }
                isNotHandyman={isNotHandyman}
                excludeFilters={[
                  'price-range',
                  'location',
                  'name',
                  'categories',
                ]}
                address={orderDetails?.address ?? null}
                radiusKm={10}
                actionButtonTitle={
                  isAuth
                    ? shouldVerify
                      ? 'Verify to select'
                      : 'Select'
                    : 'Sign in to select'
                }
                onAction={handleSelectHandyman}
              />
            </GoogleMapsWrapper>
          </DialogContent>
        </Dialog> */}

        {/* Create Review Dialog */}
        <Dialog
          open={showCreateReview}
          onClose={() => setShowCreateReview(false)}
          maxWidth='md'
          fullWidth
          PaperProps={{
            sx: {
              height: '80vh',
              maxHeight: '80vh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 92, 173, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(0, 92, 173, 0.5)',
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0, 92, 173, 0.3) rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <DialogTitle>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography variant='h6'>Write a Review</Typography>
              <IconButton
                onClick={() => setShowCreateReview(false)}
                size='small'
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <CreateReviewForm
              orderId={orderId || ''}
              reviewerId={currentUserId || ''}
              revieweeId={getRevieweeId()}
              reviewType={getReviewType()}
              onSubmit={handleSubmitReview}
              onCancel={() => setShowCreateReview(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity={snackbarSeverity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Page Content */}
        {orderDetailsLoading ? (
          <OrderDetailsSkeleton />
        ) : orderDetailsError ? (
          <Alert severity='error'>{orderDetailsError}</Alert>
        ) : orderDetails ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Order Details Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: { xs: '16px', sm: '20px', md: '24px 24px 32px' },
                gap: { xs: '16px', md: '24px' },
                width: '100%',
                background: isDark ? '#1A1A1A' : '#E5E5DC',
                boxShadow:
                  '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
                borderRadius: { xs: '24px', md: '32px' },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  width: '100%',
                  gap: '16px',
                }}
              >
                <Typography
                  sx={{
                    margin: '0 auto',
                    fontFamily: "'Montserrat', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: { xs: '20px', md: '24px' },
                    lineHeight: '133.4%',
                    textAlign: 'center',
                    color: isDark ? '#00FFFF' : '#005CAD',
                  }}
                >
                  Order Details
                </Typography>
              </Box>

              {/* Two Column Cards */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: { xs: '16px', md: '30px' },
                  width: '100%',
                }}
              >
                {/* Left Card */}
                <Box
                  sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: { xs: '16px', md: '24px' },
                    gap: { xs: '12px', md: '16px' },
                    flex: 1,
                    width: '100%',
                    background: isDark ? '#1A1A1A' : '#E5E5DC',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow:
                      '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
                    borderRadius: { xs: '24px', md: '32px' },
                  }}
                >
                  {/* Name */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                      }}
                    >
                      <InfoIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E5E5DC',
                          filter:
                            'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '157%',
                          letterSpacing: '0.1px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                        }}
                      >
                        Name
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#D5D5D5' : '#585858',
                        }}
                      >
                        {orderDetails.name ||
                          orderDetails.category?.name ||
                          'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Price */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                      }}
                    >
                      <AttachMoneyIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E5E5DC',
                          filter:
                            'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '157%',
                          letterSpacing: '0.1px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                        }}
                      >
                        Price
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#D5D5D5' : '#585858',
                        }}
                      >
                        £{orderDetails.price ?? 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Due */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                      }}
                    >
                      <AccessTimeIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E5E5DC',
                          filter:
                            'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '157%',
                          letterSpacing: '0.1px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                        }}
                      >
                        Due
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#D5D5D5' : '#585858',
                        }}
                      >
                        {orderDetails.dueDateUtc
                          ? new Date(orderDetails.dueDateUtc).toLocaleString()
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Right Card */}
                <Box
                  sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: { xs: '16px', md: '24px' },
                    gap: { xs: '12px', md: '16px' },
                    flex: 1,
                    width: '100%',
                    background: isDark ? '#1A1A1A' : '#E5E5DC',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow:
                      '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
                    borderRadius: { xs: '24px', md: '32px' },
                  }}
                >
                  {/* Description */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E5E5DC',
                          filter:
                            'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '157%',
                          letterSpacing: '0.1px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                        }}
                      >
                        Description
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#D5D5D5' : '#585858',
                        }}
                      >
                        {orderDetails.description || 'No description provided.'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Created At */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                      }}
                    >
                      <CalendarTodayIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E5E5DC',
                          filter:
                            'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '157%',
                          letterSpacing: '0.1px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                        }}
                      >
                        Created At
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#D5D5D5' : '#585858',
                        }}
                      >
                        {orderDetails.createdAtUtc
                          ? new Date(orderDetails.createdAtUtc).toLocaleString()
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '16px',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px', md: '8px' },
                        width: { xs: '40px', md: '48px' },
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        backgroundBlendMode: 'darken',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                      }}
                    >
                      <InfoIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E5E5DC',
                          filter:
                            'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.15))',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: '157%',
                          letterSpacing: '0.1px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                        }}
                      >
                        Status
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          padding: '2px 16px',
                          background:
                            orderDetails.customerStatus ===
                            CustomerOrderStatus.AwaitingProposals
                              ? '#29A322'
                              : orderDetails.customerStatus ===
                                  CustomerOrderStatus.InProgress
                                ? '#4CAF50'
                                : orderDetails.customerStatus ===
                                    CustomerOrderStatus.ActionRequired
                                  ? '#FF9800'
                                  : '#005CAD',
                          boxShadow:
                            '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
                          borderRadius: '999px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontStyle: 'normal',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textAlign: 'right',
                            color: isDark ? '#B6B6B6' : '#1A1A1A',
                          }}
                        >
                          {mapOrderStatusToString(orderDetails.customerStatus)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Handyman Section - Only show for customers */}
              {role === 'customer' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Roboto', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '16px',
                      lineHeight: '175%',
                      letterSpacing: '0.15px',
                      color: '#1A1A1A',
                    }}
                  >
                    Handyman
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                    }}
                  >
                    {orderDetails.handyman && (
                      <ClickableAvatar
                        userId={orderDetails.handyman.id}
                        src={
                          orderDetails.handyman.avatarUrl ||
                          'https://via.placeholder.com/40'
                        }
                        alt={`${orderDetails.handyman.firstName} ${orderDetails.handyman.lastName}`}
                        sx={{ width: 40, height: 40 }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontFamily: "'Roboto', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '150%',
                        letterSpacing: '0.15px',
                        color: isDark ? '#B6B6B6' : '#47536B',
                      }}
                    >
                      {orderDetails.handyman
                        ? `${orderDetails.handyman.firstName} ${orderDetails.handyman.lastName}`
                        : 'No handyman selected yet'}
                    </Typography>
                    {handymenInvited.length > 0 && !orderDetails.handyman && (
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#B6B6B6' : '#47536B',
                        }}
                      >
                        Invited handymen:{' '}
                        {handymenInvited
                          .map((h) => h.fullName)
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                    )}

                    {handymenApplied.length > 0 && (
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#B6B6B6' : '#47536B',
                        }}
                      >
                        Applied handymen:{' '}
                        {handymenApplied
                          .map((h) => h.fullName)
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Customer Section - Only show for handymen */}
              {role === 'handyman' && orderDetails.customer && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '8px',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Roboto', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '16px',
                      lineHeight: '175%',
                      letterSpacing: '0.15px',
                      color: isDark ? '#D5D5D5' : '#1A1A1A',
                    }}
                  >
                    Customer
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                    }}
                  >
                    <ClickableAvatar
                      userId={orderDetails.customer.id}
                      src={
                        orderDetails.customer.avatarUrl ||
                        'https://via.placeholder.com/40'
                      }
                      alt={`${orderDetails.customer.firstName} ${orderDetails.customer.lastName}`}
                      sx={{ width: 40, height: 40 }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "'Roboto', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '150%',
                        letterSpacing: '0.15px',
                        color: isDark ? '#B6B6B6' : '#47536B',
                      }}
                    >
                      {orderDetails.customer.firstName}{' '}
                      {orderDetails.customer.lastName}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Order Photos Section */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: '12px',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '29px',
                    color: isDark ? '#D5D5D5' : '#000000',
                  }}
                >
                  Order Photos
                </Typography>
                {orderDetails.base64Images &&
                orderDetails.base64Images.length > 0 ? (
                  <Grid container spacing={2}>
                    {orderDetails.base64Images.map((image, index) => (
                      <Grid item key={index} xs={6} sm={4} md={3}>
                        <Box
                          onClick={() => handleImageClick(image)}
                          sx={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '100%', // Square aspect ratio
                            cursor: 'pointer',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: `1px solid ${
                              isDark
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)'
                            }`,
                            transition:
                              'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                          }}
                        >
                          <img
                            src={image}
                            alt={`Order image ${index + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '20px',
                      color: '#47536B',
                    }}
                  >
                    No images available.
                  </Typography>
                )}
              </Box>
            </Box>

            {renderOrderActions()}

            {getReviewsSection()}

            {role === 'customer' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: { xs: '16px', sm: '20px', md: '24px 24px 32px' },
                  gap: { xs: '16px', md: '24px' },
                  width: '100%',
                  background: isDark ? '#1A1A1A' : '#E5E5DC',
                  boxShadow:
                    '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
                  borderRadius: { xs: '24px', sm: '32px', md: '64px' },
                }}
              >
                {/* Proposals Header */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'center', sm: 'flex-end' },
                    width: '100%',
                    gap: { xs: '12px', md: '16px' },
                  }}
                >
                  <Typography
                    sx={{
                      margin: { xs: '0', sm: '0 auto' },
                      fontFamily: "'Montserrat', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: { xs: '20px', md: '24px' },
                      lineHeight: '133.4%',
                      textAlign: 'center',
                      color: isDark ? '#00FFFF' : '#005CAD',
                    }}
                  >
                    Proposals
                  </Typography>
                  <Link
                    to={`/proposals/order/${orderId}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Box
                      component='button'
                      onClick={() => {}}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: '6px 16px', md: '8px 24px' },
                        gap: '8px',
                        margin: '0 auto',
                        height: { xs: '40px', md: '48px' },
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.7)'
                          : 'rgba(0, 36, 58, 0.8)',
                        boxShadow:
                          '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                        backdropFilter: 'blur(6px)',
                        borderRadius: '64px',
                        border: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          background: isDark
                            ? 'rgba(0, 92, 173, 0.85)'
                            : 'rgba(0, 36, 58, 0.9)',
                        },
                      }}
                    >
                      <HandshakeIcon
                        sx={{
                          width: { xs: '20px', md: '24px' },
                          height: { xs: '20px', md: '24px' },
                          color: '#E2F7FF',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: { xs: '14px', md: '16px' },
                          lineHeight: '32px',
                          textAlign: 'center',
                          color: '#FFFFFF',
                          display: { xs: 'none', sm: 'block' },
                        }}
                      >
                        View all proposals
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '32px',
                          textAlign: 'center',
                          color: '#FFFFFF',
                          display: { xs: 'block', sm: 'none' },
                        }}
                      >
                        View all
                      </Typography>
                    </Box>
                  </Link>
                </Box>

                {/* Proposals List */}
                {orderProposalsLoading ? (
                  <Box
                    display='flex'
                    justifyContent='center'
                    p={2}
                    width='100%'
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : orderProposals.length === 0 ? (
                  <Typography
                    sx={{
                      color: 'rgba(26, 26, 26, 0.7)',
                      fontFamily: '"Roboto", sans-serif',
                      p: 2,
                    }}
                  >
                    No proposals yet
                  </Typography>
                ) : (
                  orderProposals.slice(0, 2).map((proposal) => (
                    <Box
                      key={proposal.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                        padding: { xs: '16px', md: '24px' },
                        gap: { xs: '12px', md: '16px' },
                        width: '100%',
                        background: isDark ? '#1A1A1A' : '#E5E5DC',
                        boxShadow:
                          '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
                        borderRadius: { xs: '24px', md: '32px' },
                      }}
                    >
                      {/* Proposal Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'flex-start' },
                          width: '100%',
                          gap: { xs: '12px', md: '16px' },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '15.82px',
                          }}
                        >
                          <ClickableAvatar
                            userId={proposal?.handyman?.id}
                            src={
                              proposal?.handyman?.avatarUrl ||
                              'https://via.placeholder.com/40'
                            }
                            sx={{
                              width: '40px',
                              height: '40px',
                              boxShadow:
                                '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
                            }}
                          />

                          {/* Name and Rating */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'flex-start',
                              gap: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "'Montserrat', sans-serif",
                                  fontStyle: 'normal',
                                  fontWeight: 800,
                                  fontSize: '20px',
                                  lineHeight: '160%',
                                  letterSpacing: '0.15px',
                                  color: isDark ? '#B6B6B6' : '#1A1A1A',
                                }}
                              >
                                {proposal?.handyman
                                  ? `${proposal.handyman.firstName} ${proposal.handyman.lastName}`
                                  : 'N/A'}
                              </Typography>
                              {/* Diamond Icon for Verified */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '24px',
                                  height: '24px',
                                }}
                              >
                                <svg
                                  width='24'
                                  height='24'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                >
                                  <path
                                    d='M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z'
                                    fill='#005CAD'
                                  />
                                </svg>
                              </Box>
                            </Box>

                            {/* Rating Stars */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: '4px',
                                  filter:
                                    'drop-shadow(0px 1.07796px 2.48379px #000000) drop-shadow(0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4))',
                                }}
                              >
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIcon
                                    key={star}
                                    sx={{
                                      width: '18px',
                                      height: '18px',
                                      color: '#D1AB01',
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {/* Status and Time */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '16px',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              padding: '2px 16px',
                              background: '#005CAD',
                              boxShadow:
                                '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
                              borderRadius: '999px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontStyle: 'normal',
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: '18px',
                                textAlign: 'right',
                                color: '#E5E5DC',
                              }}
                            >
                              Sent
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: "'Montserrat', sans-serif",
                              fontStyle: 'normal',
                              fontWeight: 500,
                              fontSize: '12px',
                              lineHeight: '166%',
                              textAlign: 'right',
                              letterSpacing: '0.4px',
                              color: isDark ? '#B6B6B6' : '#1A1A1A',
                            }}
                          >
                            {getDaysAgoLabel(proposal?.proposedDate)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Comment */}
                      <Typography
                        sx={{
                          fontFamily: "'Roboto', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '150%',
                          letterSpacing: '0.15px',
                          color: isDark ? '#B6B6B6' : '#1A1A1A',
                          width: '100%',
                        }}
                      >
                        No comment provided.
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant='h6' color='text.secondary'>
            Order not found.
          </Typography>
        )}

        {/* Proposal Price Modal */}
        {orderDetails && (
          <ProposalPriceModal
            open={showProposalModal}
            order={orderDetails}
            onClose={handleProposalModalClose}
            onSubmit={handleProposalSubmit}
            loading={applyingToOrder}
          />
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  orderDetails: state.order.orderDetails,
  orderDetailsLoading: state.order.orderDetailsLoading,
  orderDetailsError: state.order.orderDetailsError,
  orderProposals: state.order.orderProposals,
  orderProposalsLoading: state.order.orderProposalsLoading,
  handymanProposals: state.order.handymanProposals,
  handymanProposalsLoading: state.order.handymanProposalsLoading,
  handymanOrderProposals: state.order.handymanOrderProposals,
  handymanOrderProposalsLoading: state.order.handymanOrderProposalsLoading,
  role: state.user?.authData?.role ?? 'customer',
  currentUserId: state.user?.currentUserDetails?.id,
  orderReviews: state.review.orderReviews,
  orderReviewsLoading: state.review.orderReviewsLoading,
  orderReviewsTotalCount: state.review.orderReviewsTotalCount,
  orderReviewsError: state.review.orderReviewsError,
  currentPage: state.review.currentPage,
});

export default connect(mapStateToProps, {
  getOrderDetails,
  getOrderProposals,
  updateOrderProposal,
  sendOrderProposal,
  getOrderReviews,
})(OrderDetailsPage);
